import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Moon, Sparkles, ArrowRight, Check } from "lucide-react";
import { usePageMeta } from "@/lib/seo";

const LAYERS = [
  { num: "Layer One", title: "The Ancestral Layer", sub: "What you inherited" },
  { num: "Layer Two", title: "The Karmic Layer", sub: "What you carry" },
  { num: "Layer Three", title: "The Present Layer", sub: "Your capacity" },
];

export default function WealthBlueprint() {
  usePageMeta(
    "The Wealth Blueprint (Free) — Just be you by Mehjabin",
    "3 patterns quietly shaping your money story — the Ancestral, Karmic and Present layers, decoded. A free guide by Mehjabin, delivered to your inbox."
  );
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email, name, tag: "wealth_blueprint" });
      setDone(true);
    } catch {
      toast.error("Something interrupted that. Try again?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ink min-h-screen" data-testid="wealth-blueprint-page">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.2, 0.7, 0.3, 1] }}
          className="border border-amber/40 rounded-[1.5rem] px-6 py-14 md:px-14 md:py-20 text-center relative overflow-hidden"
        >
          <Moon size={22} className="text-amber mx-auto mb-8" strokeWidth={1.2} />
          <div className="text-amber text-[11px] md:text-xs uppercase tracking-[0.28em] font-sans font-semibold mb-6">
            A guide for those ready to build on solid ground
          </div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-canvas leading-[1.05] tracking-tight">
            The Wealth Blueprint
          </h1>
          <p className="font-serif italic text-lg md:text-2xl text-peach-deep mt-6">
            3 patterns quietly shaping your money story
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
            {LAYERS.map((l) => (
              <div key={l.num} className="border border-canvas/10 rounded-2xl p-5 bg-canvas/[0.03]" data-testid={`wb-layer-${l.num.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="text-peach-deep text-[10px] uppercase tracking-[0.22em] font-sans font-semibold mb-2">{l.num}</div>
                <div className="font-serif text-lg text-canvas leading-snug">{l.title}</div>
                <div className="font-serif italic text-sm text-amber mt-1">{l.sub}</div>
              </div>
            ))}
          </div>

          <p className="text-canvas/70 text-sm md:text-base font-sans mt-10 max-w-md mx-auto leading-relaxed">
            All 3 layers, decoded — plus a guided pattern check to find where your own map begins.
            <span className="text-canvas/50"> Free, and it arrives instantly.</span>
          </p>

          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-10 max-w-md mx-auto border border-amber/50 rounded-2xl p-8 bg-canvas/[0.04]"
              data-testid="wb-success"
            >
              <Check size={28} className="text-amber mx-auto mb-4" strokeWidth={1.5} />
              <div className="font-serif text-2xl text-canvas">It's on its way. 🌙</div>
              <p className="text-canvas/70 text-sm font-sans mt-3 leading-relaxed">
                Check your inbox — the full Blueprint should be there in a minute or two.
                If it's shy, peek in promotions or spam and rescue it.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-10 max-w-md mx-auto space-y-3" data-testid="wb-form">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                className="w-full bg-canvas/[0.05] border border-canvas/20 rounded-full px-6 py-3.5 text-canvas placeholder:text-canvas/40 font-sans text-sm focus:outline-none focus:border-amber/60 transition-colors"
                data-testid="wb-name-input"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="w-full bg-canvas/[0.05] border border-canvas/20 rounded-full px-6 py-3.5 text-canvas placeholder:text-canvas/40 font-sans text-sm focus:outline-none focus:border-amber/60 transition-colors"
                data-testid="wb-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-peach-deep hover:bg-amber text-ink font-sans font-semibold text-sm uppercase tracking-[0.14em] rounded-full px-6 py-4 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="wb-submit-btn"
              >
                {loading ? "Sending…" : "Send me the Blueprint"}
                {!loading && <ArrowRight size={16} strokeWidth={2} />}
              </button>
              <p className="text-canvas/40 text-xs font-sans pt-1">
                No spam, ever. A soft note each new moon at most.
              </p>
            </form>
          )}

          <div className="mt-12 flex items-center justify-center gap-2 text-canvas/40 text-xs font-sans">
            <Sparkles size={12} className="text-amber" />
            <span>This isn't a manifestation guide. It's a decoding.</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
